import * as React from 'react'
import { ReactElement, useMemo, useState } from 'react'
import {
    Button,
    ButtonProps,
    Divider,
    Input,
    makeStyles,
    Popover,
    PopoverSurface,
    PopoverTrigger,
    Spinner,
    Text,
    tokens,
} from '@fluentui/react-components'
import { AddRegular, ArrowLeftRegular, BuildingRegular, DismissRegular, PersonAddRegular, PersonRegular, SearchRegular } from '@fluentui/react-icons'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import useAxios from '../../../hooks/useAxios'
import useEntity from '../../../hooks/useEntity'
import { CreateDto, FilePartyRole, MainDto } from '../../../config/filePartyConfig'
import { FilePartyRoleDto } from '../../../context/GlobalContext'
import { AddPartyProps, PartyType } from '../../../components/Form/Party/PartyForm'
import { ListItemDto } from '../../../config/partyConfig'
import { getPartyIcon } from '../../pages/Party/PartyListItem'

const useStyles = makeStyles({
    surface: {
        display: 'flex',
        flexDirection: 'column',
        rowGap: tokens.spacingVerticalXS,
        // Sidepanel-first: keep the cards readable and stop them stretching on wide desktop widths.
        width: '100%',
        maxWidth: '600px',
        maxHeight: '92dvh',
        padding: tokens.spacingVerticalS,

        '@media screen and (max-width: 400px)': {
            maxWidth: `92dvw`,
        },
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        columnGap: tokens.spacingHorizontalXS,
    },
    titleRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    title: {
        fontWeight: tokens.fontWeightSemibold,
        fontSize: tokens.fontSizeBase400,
        lineHeight: tokens.lineHeightBase400,
    },
    list: {
        display: 'flex',
        flexDirection: 'column',
        overflowY: 'auto',
    },
    item: {
        justifyContent: 'flex-start',
        // Keep a stable row height so many results scroll instead of being squeezed flat.
        flexShrink: 0,
        minHeight: '32px',
        height: 'auto',
        // Party names are not emphasized (Fluent buttons default to semibold).
        fontWeight: tokens.fontWeightRegular,
        // Allow the label to wrap (see itemLabel) and align it left.
        whiteSpace: 'normal',
        textAlign: 'left',
    },
    itemLabel: {
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        wordBreak: 'break-word',
    },
    hint: {
        color: tokens.colorNeutralForeground3,
        padding: tokens.spacingVerticalXS,
    },
})

export type PartyConnectButtonProps = {
    fileId: string
    roles: FilePartyRoleDto[]
    // When set, we are adding a contactpersoon (natural person) under this bedrijf; the role is fixed to the parent's.
    parent?: MainDto
    reset?: () => void
    appearance?: ButtonProps['appearance']
    // Render a compact icon-only trigger (used for the per-bedrijf "+ contactpersoon" action).
    iconOnly?: boolean
}

/**
 * v9 replacement for the legacy (redux/Fluent-v8) PartyConnectButton. Opens a Popover to either pick a soort and
 * then an existing party to link, or navigate to the add-party forms. Redux-free: links via axios and refreshes
 * the list through the `reset` callback (the parent loads parties with useEntity).
 */
export default function PartyConnectButton(props: PartyConnectButtonProps): ReactElement {
    const styles = useStyles()
    const { t: translate } = useTranslation()
    const history = useHistory()
    const axios = useAxios()

    const [open, setOpen] = useState(false)
    const [selectedRole, setSelectedRole] = useState<FilePartyRole | string>()
    const [query, setQuery] = useState('')

    const role = props.parent ? props.parent.role : selectedRole
    const showPartyStep = Boolean(props.parent || role)

    const [partyList] = useEntity<ListItemDto[]>(
        () => ({ url: '/party/list' }),
        open ? false : undefined,
        result => result.data,
        [],
        undefined,
        [open],
    )
    const [suggestions] = useEntity<ListItemDto[]>(
        () => ({ url: `/file-party/${props.parent?.pivotId}/suggest` }),
        open && props.parent ? false : undefined,
        result => result.data,
        [],
        undefined,
        [open],
    )

    const close = () => {
        setOpen(false)
        setSelectedRole(undefined)
        setQuery('')
    }

    const filteredParties = useMemo(() => {
        const normalized = query.trim().toLowerCase()
        if (normalized.length < 3) {
            return []
        }
        return (partyList.data || [])
            .filter(party => !props.parent || party.type === PartyType.Natural)
            .filter(party => party.displayName?.toLowerCase().includes(normalized))
    }, [partyList.data, query, props.parent])

    const linkParty = (partyId: string) => {
        if (!role) {
            return
        }
        const payload: CreateDto = { fileId: props.fileId, partyId, role, ...(props.parent ? { parentId: props.parent.pivotId } : {}) }
        axios.post('/file-party', payload).then(() => {
            props.reset?.()
            close()
        })
    }

    const addNew = (type: PartyType) => {
        if (!role) {
            return
        }
        const state: AddPartyProps = { role, parentId: props.parent?.pivotId }
        history.push(`/file/${props.fileId}/party/${type.toLowerCase()}/add`, state)
        close()
    }

    const roleLabel = useMemo(() => props.roles?.find(r => r.type === role)?.label, [props.roles, role])

    return (
        <Popover
            open={open}
            onOpenChange={(_event, data) => (data.open ? setOpen(true) : close())}
            trapFocus
            // Anchor to the trigger and open above it (over the content, not the side panels). Without an explicit
            // positioning Fluent can fall back to the top-left corner when the trigger ref detaches.
            positioning={{ align: 'start' }}
        >
            <PopoverTrigger disableButtonEnhancement>
                {props.iconOnly ? (
                    <Button
                        appearance={props.appearance ?? 'subtle'}
                        icon={props.parent ? <PersonAddRegular /> : <AddRegular />}
                        aria-label={props.parent ? translate('file.pivot.party.add.contact') : translate('file.pivot.party.add')}
                    />
                ) : (
                    <Button appearance={props.appearance ?? 'primary'} icon={<AddRegular />}>
                        {translate('file.pivot.party.add')}
                    </Button>
                )}
            </PopoverTrigger>

            <PopoverSurface className={styles.surface}>
                {!showPartyStep ? (
                    <>
                        <div className={styles.titleRow}>
                            <Text className={styles.title}>{translate('file.pivot.party.add')}</Text>
                            <Button
                                appearance="subtle"
                                size="small"
                                icon={<DismissRegular />}
                                aria-label={translate('action.close')}
                                onClick={close}
                            />
                        </div>
                        <div className={styles.list}>
                            {props.roles
                                ?.filter(candidate => candidate.type)
                                .map(candidate => (
                                    <Button
                                        key={candidate.type}
                                        appearance="subtle"
                                        className={styles.item}
                                        onClick={() => setSelectedRole(candidate.type)}
                                    >
                                        {candidate.label}
                                    </Button>
                                ))}
                        </div>
                    </>
                ) : (
                    <>
                        <div className={styles.titleRow}>
                            <div className={styles.header}>
                                {!props.parent && (
                                    <Button
                                        appearance="subtle"
                                        size="small"
                                        icon={<ArrowLeftRegular />}
                                        aria-label={translate('base.back')}
                                        onClick={() => setSelectedRole(undefined)}
                                    />
                                )}
                                <Text className={styles.title}>{roleLabel ?? translate('file.pivot.party.add.contact')}</Text>
                            </div>
                            <Button
                                appearance="subtle"
                                size="small"
                                icon={<DismissRegular />}
                                aria-label={translate('action.close')}
                                onClick={close}
                            />
                        </div>

                        {!props.parent && (
                            <>
                                <Button
                                    appearance="subtle"
                                    className={styles.item}
                                    icon={<BuildingRegular />}
                                    onClick={() => addNew(PartyType.Legal)}
                                >
                                    {translate('file.pivot.party.add.legal')}
                                </Button>
                                <Button
                                    appearance="subtle"
                                    className={styles.item}
                                    icon={<PersonRegular />}
                                    onClick={() => addNew(PartyType.Natural)}
                                >
                                    {translate('file.pivot.party.add.person')}
                                </Button>
                            </>
                        )}

                        <Input
                            contentBefore={<SearchRegular />}
                            placeholder={translate(props.parent ? 'file.pivot.party.search.contact' : 'file.pivot.party.search')}
                            value={query}
                            onChange={(_event, data) => setQuery(data.value)}
                            autoFocus
                        />

                        <div className={styles.list}>
                            {partyList.loading ? (
                                <Spinner size="tiny" />
                            ) : query.trim().length < 3 ? (
                                <Text className={styles.hint}>{translate('file.pivot.party.search.instructions')}</Text>
                            ) : filteredParties.length === 0 ? (
                                <Text className={styles.hint}>{translate('file.pivot.party.search.no_content')}</Text>
                            ) : (
                                filteredParties.map(party => (
                                    <Button
                                        key={party.id}
                                        appearance="subtle"
                                        className={styles.item}
                                        icon={getPartyIcon(party)}
                                        onClick={() => linkParty(party.id)}
                                    >
                                        <Text className={styles.itemLabel}>{party.displayName}</Text>
                                    </Button>
                                ))
                            )}
                        </div>

                        {props.parent && suggestions.data?.length > 0 && (
                            <>
                                <Divider>{translate('file.pivot.party.suggestions')}</Divider>
                                <div className={styles.list}>
                                    {suggestions.data.map(party => (
                                        <Button
                                            key={party.id}
                                            appearance="subtle"
                                            className={styles.item}
                                            icon={getPartyIcon(party)}
                                            onClick={() => linkParty(party.id)}
                                        >
                                            <Text className={styles.itemLabel}>{party.displayName}</Text>
                                        </Button>
                                    ))}
                                </div>
                            </>
                        )}

                        {props.parent && (
                            // Create-new sits under the search: look for an existing contactpersoon first, create as fallback.
                            <Button
                                appearance="secondary"
                                className={styles.item}
                                icon={<PersonAddRegular />}
                                onClick={() => addNew(PartyType.Natural)}
                            >
                                {translate('file.pivot.party.add.contact.create', { company: props.parent.displayName })}
                            </Button>
                        )}
                    </>
                )}
            </PopoverSurface>
        </Popover>
    )
}
